import AssessmentOutlined from "@mui/icons-material/AssessmentOutlined";
import CleaningServicesOutlined from "@mui/icons-material/CleaningServicesOutlined";
import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import MoveToInboxOutlined from "@mui/icons-material/MoveToInboxOutlined";
import PrintOutlined from "@mui/icons-material/PrintOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import {
  Button,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { StarBorderOutlined } from "@mui/icons-material";
import { BackdropCustom } from "../../components/backdrop/Backdrop";
import { PageCard } from "../../components/pageCard/PageCard";
import { PrintPdfDialog } from "../../components/printPdfDialog/PrintPdfDialog";
import { PrintCollectorsPdfDialog } from "../../components/printPdfDialog/PrintCollectorsPdfDialog";
import { CustomNoContentTableBody } from "../../components/table/CustomNoContentTableBody/CustomNoContentTableBody";
import CustomTablePagination from "../../components/table/CustomTablePagination/CustomTablePagination";
import { FilterButtonGroup } from "../../components/table/FilterButtonGroup/FilterButtonGroup";
import { GridFilterArea } from "../../components/table/GridFilterArea/GridFilterArea";
import { TitleContainer } from "../../components/titleContainer/TitleContainer";
import { useToastr } from "../../components/toast/Toast";
import { useAuth } from "../../hooks/auth";
import { IExhibition } from "../../interfaces/IExhibition";
import { IListAllParams } from "../../interfaces/IListAllParams";
import api from "../../services/api";
import { downloadBase64Pdf, downloadBase64Zip } from "../../utils/downloadPdf";
import { getUtcOffset } from "../../utils/timezone";
import WorkspacePremiumOutlined from "@mui/icons-material/WorkspacePremiumOutlined";
import {
  ExhibitionCard,
  ExhibitionCardActions,
  ExhibitionCardContent,
} from "../exhibitionsJudges/ExhibitionsJudges.styles";

const fields = [
  {
    field: "name",
    label: "Nome",
  },
  {
    field: "details",
    label: "Observação",
  },
];

const STORAGE_KEY = "@ORQUIDEAS:homeExhibitionsFilters";

const getInitialState = (key: string, defaultValue: any) => {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved) {
    const parsed = JSON.parse(saved);
    return parsed[key] !== undefined ? parsed[key] : defaultValue;
  }
  return defaultValue;
};

const Home: React.FC = () => {
  const { signOut, user } = useAuth();
  const toastr = useToastr();
  const timeout = useRef<any>(null);
  const navigate = useNavigate();

  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printCollectorsDialogOpen, setPrintCollectorsDialogOpen] =
    useState(false);
  const [selectedExhibitionId, setSelectedExhibitionId] = useState<string>("");

  const [exhibitions, setExhibitions] = useState<IExhibition[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(() => getInitialState("perPage", 12));
  const [currentPage, setCurrentPage] = useState(() =>
    getInitialState("currentPage", 0),
  );
  const [orderField, setOrderField] = useState<string>(() =>
    getInitialState("orderField", "date"),
  );
  const [orderDirection, setOrderDirection] = useState<"asc" | "desc">(() =>
    getInitialState("orderDirection", "desc"),
  );
  const [loading, setLoading] = useState(false);

  const [gridFilterAreaOpen, setGridFilterAreaOpen] = useState(() =>
    getInitialState("gridFilterAreaOpen", false),
  );
  const [filterField, setFilterField] = useState<string>(() =>
    getInitialState("filterField", ""),
  );
  const [filterPrecision, setFilterPrecision] = useState<string>(() =>
    getInitialState("filterPrecision", "containing"),
  );
  const [filterValue, setFilterValue] = useState<string>(() =>
    getInitialState("filterValue", ""),
  );
  const [delay, setDelay] = useState(0);

  const handleToExhibitionTracking = useCallback(
    (exhibitionId: string) => {
      if (exhibitionId) {
        window.open(`/follow-up/${exhibitionId}`, "_blank");
      } else {
        toastr.error("Exposição não encontrada");
        navigate("/exhibitions");
      }
    },
    [navigate, toastr],
  );

  const handleToExhibitionResults = useCallback(
    (exhibitionId: string) => {
      if (exhibitionId) {
        window.open(`/results/${exhibitionId}`, "_blank");
      } else {
        toastr.error("Exposição não encontrada");
        navigate("/exhibitions");
      }
    },
    [navigate, toastr],
  );

  const handleToBestCollectors = useCallback(
    (exhibitionId: string) => {
      if (exhibitionId) {
        window.open(`/best-collectors/${exhibitionId}`, "_blank");
      } else {
        toastr.error("Exposição não encontrada");
        navigate("/exhibitions");
      }
    },
    [navigate, toastr],
  );

  const handleToReception = useCallback(
    (exhibitionId: string) => {
      if (!exhibitionId) {
        toastr.error("Exposição não encontrada");
        return;
      }
      navigate(`/received/exhibition/${exhibitionId}`);
    },
    [navigate, toastr],
  );

  const handleToExhibitionEdit = useCallback(
    (exhibitionId: string) => {
      if (!exhibitionId) {
        toastr.error("Exposição não encontrada");
        return;
      }
      navigate(`/exhibition/${exhibitionId}`);
    },
    [navigate, toastr],
  );

  const handleDownloadResultsReport = useCallback(
    async (id: string) => {
      setLoading(true);
      try {
        const response = await api.get(
          `/exhibitions/${id}/results/report?utcOffset=${getUtcOffset()}`,
        );
        const { filename, base64 } = response.data;
        downloadBase64Pdf(base64, filename);
      } catch (error: any) {
        if (error?.response?.status === 422) {
          toastr.warning(
            "Execute a consolidação antes de gerar o relatório. Edite a exposição e marque como Finalizada.",
          );
        } else {
          toastr.error(
            error?.response?.data?.message ||
              "Erro ao gerar relatório de resultados",
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [toastr],
  );

  const handleGenerateJudgesCertificates = useCallback(
    async (exhibitionId: string) => {
      if (!exhibitionId) return;
      setLoading(true);
      try {
        const response = await api.post(
          `/exhibitions/${exhibitionId}/evaluation/judges/certificates`,
          { judgeIds: [] },
        );
        const { zip, certificates } = response.data as {
          zip?: { filename: string; base64: string };
          certificates?: { filename: string; base64: string }[];
        };
        if (zip) {
          downloadBase64Zip(zip.base64, zip.filename);
          toastr.success("Certificados dos juízes gerados com sucesso!");
        } else if (certificates && certificates.length > 0) {
          certificates.forEach(c => {
            downloadBase64Pdf(c.base64, c.filename);
          });
          toastr.success("Certificados dos juízes gerados com sucesso!");
        } else {
          toastr.error("Nenhum certificado foi retornado");
        }
      } catch (error: any) {
        toastr.error(
          error?.response?.data?.message ||
            "Erro ao gerar certificados dos juízes",
        );
      } finally {
        setLoading(false);
      }
    },
    [toastr],
  );

  const handleDuplicateExhibition = useCallback(
    async (id: string) => {
      setLoading(true);
      try {
        const response = await api.post(`/exhibitions/${id}/duplicate`);
        toastr.success("Exposição duplicada com sucesso!");
        navigate(`/exhibition/${response.data.id}`);
      } catch (error: any) {
        toastr.error(
          error?.response?.data?.message || "Erro ao duplicar exposição",
        );
      } finally {
        setLoading(false);
      }
    },
    [navigate, toastr],
  );

  const handleListAll = useCallback(
    async ({
      perPage,
      currentPage,
      orderField,
      orderDirection,
      field,
      value,
      precision,
      delay,
    }: IListAllParams) => {
      let url = `exhibitions?perPage=${perPage}&currentPage=${
        currentPage + 1
      }&orderBy=${orderField}&orderDirection=${orderDirection}`;

      if (field && value && precision) {
        url =
          url +
          `&filterField=${field}&filterValue=${value}&precision=${precision}`;
      }

      clearInterval(timeout.current);
      timeout.current = setTimeout(async () => {
        setLoading(true);
        await api
          .get(url)
          .then(response => {
            const responseData = response.data;

            if (responseData.data?.length > 0) {
              setExhibitions(responseData.data);
              setTotalRows(responseData.totalRows);
              setLoading(false);
            } else {
              setExhibitions([]);
              setTotalRows(0);
              setPerPage(10);
              setCurrentPage(0);
              setLoading(false);
            }
          })
          .catch(error => {
            if (error.response.status === 401) {
              signOut();
              navigate("/");
            }

            toastr.error(
              error?.response?.data?.message || "Erro ao listar exposições",
            );
          })
          .finally(() => {
            setLoading(false);
          });
      }, delay || 0);
    },
    [
      timeout,
      setLoading,
      setExhibitions,
      setTotalRows,
      setPerPage,
      setCurrentPage,
      signOut,
      navigate,
      toastr,
    ],
  );

  const handlePagination = async (
    perPage: number,
    currentPage: number,
    orderField: string,
    orderDirection: "asc" | "desc",
  ) => {
    handleListAll({
      perPage,
      currentPage,
      orderField,
      orderDirection,
      field: filterField,
      value: filterValue,
      precision: filterPrecision,
      delay: 0,
    });
  };

  useEffect(() => {
    const field = filterField;
    const value = filterValue;
    const precision = filterPrecision;

    handleListAll({
      perPage,
      currentPage,
      orderField,
      orderDirection,
      field,
      value,
      precision,
      delay,
    });
  }, [
    filterField,
    filterPrecision,
    filterValue,
    delay,
    currentPage,
    orderDirection,
    orderField,
    perPage,
    handleListAll,
  ]);

  const handleTextFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterValue(e.target.value);
    setTotalRows(0);
    setCurrentPage(0);
    setDelay(500);
  };

  const saveFiltersToStorage = useCallback(() => {
    const filters = {
      filterField,
      filterPrecision,
      filterValue,
      gridFilterAreaOpen,
      currentPage,
      perPage,
      orderField,
      orderDirection,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  }, [
    filterField,
    filterPrecision,
    filterValue,
    gridFilterAreaOpen,
    currentPage,
    perPage,
    orderField,
    orderDirection,
  ]);

  useEffect(() => {
    saveFiltersToStorage();
  }, [
    filterField,
    filterPrecision,
    filterValue,
    gridFilterAreaOpen,
    currentPage,
    perPage,
    saveFiltersToStorage,
  ]);

  const handleClearFilters = () => {
    setFilterField("");
    setFilterPrecision("containing");
    setFilterValue("");
    setCurrentPage(0);
    setPerPage(12);
    setOrderField("date");
    setOrderDirection("desc");
    localStorage.removeItem(STORAGE_KEY);
  };

  return (
    <>
      <TitleContainer>
        <h1>Home</h1>
      </TitleContainer>
      <PageCard>
        <FilterButtonGroup>
          <div>
            <Tooltip title="Filtros" placement="top">
              <IconButton
                onClick={() => setGridFilterAreaOpen(!gridFilterAreaOpen)}
                size="small"
                color="primary"
                style={{ alignItems: "flex-end" }}
              >
                {gridFilterAreaOpen ? (
                  <FilterAltOffOutlinedIcon />
                ) : (
                  <FilterAltOutlinedIcon />
                )}
              </IconButton>
            </Tooltip>

            {gridFilterAreaOpen && (
              <Tooltip title="Limpar Filtros" placement="top">
                <IconButton
                  onClick={() => handleClearFilters()}
                  size="small"
                  color="primary"
                  style={{ alignItems: "flex-end" }}
                >
                  <CleaningServicesOutlined />
                </IconButton>
              </Tooltip>
            )}
          </div>
          <div></div>
        </FilterButtonGroup>

        {gridFilterAreaOpen && (
          <GridFilterArea container spacing={2}>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="select-filter">Filtrar por</InputLabel>
                <Select
                  label="Filtrar Por"
                  value={`${filterField}`}
                  onChange={e => {
                    setFilterField(`${e.target.value}`);
                  }}
                >
                  {fields.map(filter => {
                    return (
                      <MenuItem key={filter.field} value={filter.field}>
                        {filter.label}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Precisão</InputLabel>
                <Select
                  label="Precisão"
                  value={`${filterPrecision}`}
                  onChange={e => {
                    setFilterPrecision(`${e.target.value}`);
                  }}
                >
                  <MenuItem key={"equal"} value={"equal"}>
                    Igual
                  </MenuItem>
                  <MenuItem key={"containing"} value={"containing"}>
                    Contendo
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Valor a filtrar"
                placeholder="Valor a filtrar"
                value={filterValue}
                onChange={handleTextFilterChange}
                size="small"
              />
            </Grid>
          </GridFilterArea>
        )}

        <Grid
          container
          spacing={2}
          sx={{ marginBottom: "20px", padding: "8px" }}
        >
          {exhibitions.length > 0 &&
            exhibitions.map(exhibition => {
              return (
                <Grid item xs={12} md={4} sm={6} lg={3} key={exhibition.id}>
                  <ExhibitionCard key={exhibition.id}>
                    <ExhibitionCardContent>
                      <h1>{exhibition.name}</h1>

                      <p>
                        {exhibition.date
                          ? dayjs(exhibition.date).format("DD/MM/YYYY")
                          : ""}
                      </p>

                      <p>{exhibition.details || "Sem observação"}</p>

                      <span>
                        {exhibition.city} - {exhibition.stateId}
                      </span>
                    </ExhibitionCardContent>

                    <ExhibitionCardActions>
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          flexWrap: "wrap",
                          justifyContent: "flex-end",
                        }}
                      >
                        {(user.typeId === 1 || user.typeId === 4) && (
                          <>
                            {exhibition.allowRegistration && (
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<MoveToInboxOutlined />}
                                onClick={() =>
                                  handleToReception(exhibition.id || "")
                                }
                                sx={{ fontSize: "0.75rem" }}
                              >
                                Recebimento
                              </Button>
                            )}
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<PrintOutlined />}
                              onClick={() => {
                                setSelectedExhibitionId(exhibition.id || "");
                                setPrintDialogOpen(true);
                              }}
                              sx={{ fontSize: "0.75rem" }}
                            >
                              Relat. Plantas
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<PrintOutlined />}
                              onClick={() => {
                                setSelectedExhibitionId(exhibition.id || "");
                                setPrintCollectorsDialogOpen(true);
                              }}
                              sx={{ fontSize: "0.75rem" }}
                            >
                              Relat. Colecionadores
                            </Button>
                          </>
                        )}
                        {user.typeId === 1 && (
                          <>
                            {exhibition.finished && (
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<PrintOutlined />}
                                onClick={() =>
                                  handleDownloadResultsReport(
                                    exhibition.id || "",
                                  )
                                }
                                sx={{ fontSize: "0.75rem" }}
                              >
                                Relat. Resultados
                              </Button>
                            )}

                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<VisibilityOutlined />}
                              onClick={() =>
                                handleToExhibitionTracking(exhibition.id || "")
                              }
                              sx={{ fontSize: "0.75rem" }}
                            >
                              Acompanhar
                            </Button>

                            {exhibition.finished && (
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<AssessmentOutlined />}
                                onClick={() =>
                                  handleToExhibitionResults(exhibition.id || "")
                                }
                                sx={{ fontSize: "0.75rem" }}
                              >
                                Resultados
                              </Button>
                            )}
                            {exhibition.finished && (
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<StarBorderOutlined />}
                                onClick={() =>
                                  handleToBestCollectors(exhibition.id || "")
                                }
                                sx={{ fontSize: "0.75rem" }}
                              >
                                Melhores Colecionadores
                              </Button>
                            )}
                            {user?.typeId === 1 && exhibition.finished && (
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<WorkspacePremiumOutlined />}
                                onClick={() =>
                                  handleGenerateJudgesCertificates(
                                    exhibition.id || "",
                                  )
                                }
                                sx={{ fontSize: "0.75rem" }}
                              >
                                Certificado Juizes
                              </Button>
                            )}
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<EditOutlined />}
                              onClick={() =>
                                handleToExhibitionEdit(exhibition.id || "")
                              }
                              sx={{ fontSize: "0.75rem" }}
                            >
                              Editar
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<ContentCopyOutlined />}
                              onClick={() =>
                                handleDuplicateExhibition(exhibition.id || "")
                              }
                              sx={{ fontSize: "0.75rem" }}
                            >
                              Duplicar
                            </Button>
                          </>
                        )}
                      </div>
                    </ExhibitionCardActions>
                  </ExhibitionCard>
                </Grid>
              );
            })}
        </Grid>

        {exhibitions.length === 0 && (
          <CustomNoContentTableBody description="Nenhuma exposição encontrada" />
        )}

        <CustomTablePagination
          totalRows={totalRows}
          currentPage={currentPage}
          perPage={perPage}
          handleRequest={handlePagination}
          setPerPage={setPerPage}
          setCurrentPage={setCurrentPage}
          orderField={orderField}
          orderDirection={orderDirection}
        />
      </PageCard>

      <PrintPdfDialog
        open={printDialogOpen}
        onClose={() => setPrintDialogOpen(false)}
        exhibitionId={selectedExhibitionId}
        allowSplit
        mode="download"
      />

      <PrintCollectorsPdfDialog
        open={printCollectorsDialogOpen}
        onClose={() => setPrintCollectorsDialogOpen(false)}
        exhibitionId={selectedExhibitionId}
      />

      {loading && <BackdropCustom />}
    </>
  );
};

export { Home };
